import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { handleSalesforceCallback } from '../services/salesforceService'
import { supabase } from '../services/supabaseClient'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'

export default function SalesforceCallback() {
  const { user } = useAuth()
  const [status, setStatus] = useState('Processing your Salesforce connection...')
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const navigate = useNavigate()
  
  useEffect(() => {
    // Wait for user to be authenticated before processing callback
    if (!user) {
      console.log('Waiting for user authentication before processing callback...')
      return
    }

    // Prevent multiple executions
    if (isProcessing) {
      console.log('Already processing callback, skipping...')
      return
    }

    const handleCallback = async () => {
      setIsProcessing(true)
      // Get the authorization code and state from URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const errorParam = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')
      
      // Clear URL parameters to prevent re-processing on refresh
      window.history.replaceState({}, document.title, window.location.pathname)
      
      // Check for errors from Salesforce
      if (errorParam) {
        console.error('OAuth error received:', { error: errorParam, description: errorDescription })
        setError(errorDescription || errorParam || 'An error occurred during authentication')
        return
      }
      
      // Check for missing parameters
      if (!code) {
        console.error('No authorization code received')
        setError('No authorization code received from Salesforce')
        return
      }
      
      if (!state) {
        console.error('No state parameter received')
        setError('Security error: No state parameter received')
        return
      }
      
      // Parse state parameter
      let stateData: any = {};
      try {
        // Decode base64 state (browser-compatible)
        stateData = JSON.parse(atob(state));
        console.log('Successfully decoded state:', stateData);
      } catch (e) {
        console.log('Could not decode state as base64 JSON:', e);
      }
      
      // Check if this is a re-authentication flow
      const reauthOrgId = sessionStorage.getItem('reauth_org_id');
      console.log('Checking reauth flow - stateData.isReauth:', stateData.isReauth, 'reauthOrgId:', reauthOrgId);
      
      // Check both stateData.isReauth and if state matches the stored reauth org ID
      if (stateData.isReauth || (reauthOrgId && state === reauthOrgId)) {
        const orgId = stateData.orgId || reauthOrgId;
        console.log('Re-authentication flow detected for org:', orgId);
        
        try {
          setStatus('Re-authenticating with Salesforce...');
          
          // Get the code verifier from session storage
          const codeVerifier = sessionStorage.getItem('salesforce_code_verifier');
          
          // Get current session for auth
          const { data: { session } } = await supabase.auth.getSession();
          
          // Call the refresh-org-auth endpoint with the code
          const endpoint = '/api/salesforce/refresh-org-auth';
          let result;
          
          if (isEdgeFunctionMigrated(endpoint)) {
            // Use Edge Function
            const functionName = getEdgeFunctionName(endpoint);
            result = await callEdgeFunction(functionName!, {
              orgId: orgId,
              code: code,
              codeVerifier: codeVerifier,
              redirectUri: import.meta.env.VITE_SALESFORCE_CALLBACK_URL
            });
          } else {
            // Fallback to Vercel API
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': session ? `Bearer ${session.access_token}` : ''
              },
              body: JSON.stringify({
                orgId: orgId,
                code: code,
                codeVerifier: codeVerifier,
                redirectUri: import.meta.env.VITE_SALESFORCE_CALLBACK_URL
              })
            });
            
            if (!response.ok) {
              throw new Error('Failed to refresh authentication');
            }
            
            result = await response.json();
          }
          
          // Get the return URL from state or session storage
          const returnUrl = stateData.returnUrl || sessionStorage.getItem('reauth_return_url');
          
          // Clear the session storage
          sessionStorage.removeItem('reauth_org_id');
          sessionStorage.removeItem('salesforce_code_verifier');
          sessionStorage.removeItem('reauth_return_url');
          
          setStatus('Authentication refreshed successfully! Redirecting...');
          
          // Clear URL parameters to prevent re-processing
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Redirect back to original page or flows page
          setTimeout(() => {
            if (returnUrl) {
              navigate(returnUrl);
            } else {
              navigate(`/flows?orgId=${orgId}`);
            }
          }, 1500);
          
          return;
        } catch (error) {
          console.error('Re-authentication error:', error);
          setError('Failed to refresh authentication. Please try again.');
          return;
        }
      }
      
      // Log the callback parameters
      console.log('Processing Salesforce callback with code and state')
      
      try {
        setStatus('Exchanging authorization code for tokens...')
        
        // Handle the Salesforce callback using the new unified function
        const result = await handleSalesforceCallback(code, state)
        
        if (result.success && result.orgId) {
          setStatus('Successfully connected to Salesforce! Starting sync...')
          
          // Immediately trigger sync for the new org
          try {
            setStatus('Starting flow synchronization...')
            
            // Get the current session to include auth headers
            const { data: { session } } = await supabase.auth.getSession()
            const authHeaders: any = {
              'Content-Type': 'application/json'
            }
            if (session?.access_token) {
              authHeaders['Authorization'] = `Bearer ${session.access_token}`
              authHeaders['x-supabase-auth'] = session.access_token
            }
            
            // Trigger flow sync via Edge Function
            console.log('Triggering flow sync for org:', result.orgId)
            setStatus('Syncing flows from Salesforce...')
            
            const { data: syncData, error: syncError } = await supabase.functions.invoke('salesforce-sync-streamlined', {
              body: { 
                orgId: result.orgId,
                priority: 'critical' // New org sync gets priority
              }
            })
            
            console.log('Edge Function response:', { syncData, syncError })
            if (syncData) {
              console.log('Sync data details:', JSON.stringify(syncData, null, 2))
            }
            
            if (syncError) {
              console.error('Error during sync:', syncError)
              // Try to get more details about the error
              if (syncError instanceof Error && 'context' in syncError) {
                const context = (syncError as any).context
                console.error('Error details:', context)
                // Try to parse the response body if available
                if (context?.body) {
                  try {
                    const bodyText = await context.text()
                    console.error('Error response body:', bodyText)
                  } catch (e) {
                    console.error('Could not read error body')
                  }
                }
              }
              setStatus('Flow sync failed, but org is connected.')
            } else if (syncData?.success) {
              // Handle new queued response
              if (syncData.syncStatus?.status === 'queued') {
                console.log('Flow sync queued:', syncData)
                setStatus('Flow sync has been queued and will process in the background.')
              } else {
                // Handle legacy direct sync response
                const results = syncData.results || {}
                console.log('Flow sync completed:', results)
                setStatus(`Flow sync completed! ${results.total || 0} flows found, ${results.processed || 0} processed.`)
              }
            } else {
              console.log('Sync returned without error but no success flag:', syncData)
              setStatus('Flow sync status unknown, but org is connected.')
            }
            
          } catch (syncError) {
            console.error('Error during sync:', syncError)
            // Don't throw - still navigate to orgs page
          }
          
          // Navigate to the orgs page to show sync status
          console.log('Navigating to orgs page with syncingOrg:', result.orgId)
          setTimeout(() => {
            navigate(`/orgs?syncingOrg=${result.orgId}`)
          }, 1500)
        } else {
          throw new Error('Failed to complete Salesforce connection')
        }
      } catch (err) {
        console.error('Error during Salesforce connection:', err)
        setError(err instanceof Error ? err.message : 'An error occurred while connecting to Salesforce')
      }
    }
    
    handleCallback()
  }, [navigate, isProcessing, user])
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Connection Failed
            </h2>
            <p className="mt-2 text-sm text-red-600">
              {error}
            </p>
            <button
              onClick={() => navigate('/dashboard/orgs')}
              className="mt-4 group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Return to Organizations
            </button>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Connecting to Salesforce
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {status}
          </p>
        </div>
      </div>
    </div>
  )
}